import { Button } from "devextreme-react/button";
import { Column, DataGrid, Editing, Lookup, Paging, Scrolling, Selection } from "devextreme-react/data-grid";
import { SelectBox } from "devextreme-react/select-box";
import { TextBox } from "devextreme-react/text-box";
import { Box, Item } from "devextreme-react/box";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useContext, useRef, useState } from "react";
import { useGetProductLines, useGetProductTypes } from "../../../../api/product";
import { Field } from "../../../../layouts";
import { MaterialOrderContext } from "../context/MaterialOrderContext";
import { apiSearchMaterialOrderProducts } from "../api/search-material-order-products"
import { showErrorMessage } from "../../../../utils/messages";
import { MOCatalogProduct } from "../types/MOCatalogProduct"
import { ProductCatalog, ProductLine } from "../../../../types/enums";
import { MaterialOrderProductSearchResult } from "../types/MaterialOrderProductSearchResult";
import { OpenedEvent } from "devextreme/ui/select_box";
import { alert } from "devextreme/ui/dialog";
import { LoadPopupPanel } from "../../../../components";
import { getClosestDeliveryDate, validateDeliveryDate } from "../functions/deliveryDate";

export default function MaterialOrderProductPopup(props: {
	visible: boolean,
	onPopupHidden: () => void,
	onProductsAdded: (newProducts: MOCatalogProduct[]) => void
}) {

	const { order } = useContext(MaterialOrderContext);

	const [processing, setProcessing] = useState(false);

	const [searchCriteria, setSearchCriteria] = useState<
		{ productLineID: number, productTypeID?: number, sku?: string, productDescription?: string }>(
			{ productLineID: order.details.productLineID ?? ProductLine.Granite });

	const [searchResults, setSearchResults] = useState<MaterialOrderProductSearchResult>();

	const productLines = useGetProductLines(props.visible);
	const productTypes = useGetProductTypes(ProductCatalog.Material, searchCriteria.productLineID, props.visible);

	const searchResultsRef = useRef<DataGrid>(null);

	let isLaminate = searchCriteria.productLineID === ProductLine.LaminateInstall || searchCriteria.productLineID === ProductLine.LaminateDelivered;

	return (
		<Popup
			title="Add Material Order Product"
			visible={props.visible}
			hideOnOutsideClick={true}
			onHiding={() => props.onPopupHidden()}
		>
			<ToolbarItem
				options={{
					icon: 'dx-icon dx-icon-close',
					onClick: function () {
						props.onPopupHidden();
					}
				}}
				widget="dxButton"
				location="after"
			/>
			<Box className="product-search-filter field-label-25">
				<Item ratio={1}>
					<Field label={'Product Line'}>
						<SelectBox elementAttr={{ 'data-testid': 'dd-product-line' }}
							showClearButton={false}
							dataSource={productLines.data}
							valueExpr={'productLineID'}
							displayExpr={'description'}
							value={searchCriteria.productLineID}
							onValueChanged={(e) => {
								setSearchCriteria({ ...searchCriteria, productLineID: e.value });
								setSearchResults({} as MaterialOrderProductSearchResult);
							}}
						/>
					</Field>
					<Field label={'Product Type'}>
						<SelectBox elementAttr={{ 'data-testid': 'dd-product-type' }}
							showClearButton={true}
							dataSource={productTypes.data}
							valueExpr={'productTypeID'}
							displayExpr={'description'}
							value={searchCriteria.productTypeID}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, productTypeID: e.value })}
						/>
					</Field>
				</Item>
				<Item ratio={1}>
					<Field label={'SKU'}>
						<TextBox inputAttr={{ 'data-testid': 'txt-sku' }}
							showClearButton={true}
							value={searchCriteria.sku}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, sku: e.value })}
						></TextBox>
					</Field>
					<Field label={'Product Description'}>
						<TextBox inputAttr={{ 'data-testid': 'txt-product-description' }}
							showClearButton={true}
							value={searchCriteria.productDescription}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, productDescription: e.value })}
						></TextBox>
					</Field>
				</Item>
				<Item ratio={0} baseSize={100}>
					<Button
						text="Search"
						elementAttr={{ 'data-testid': 'btn-search' }}
						onClick={() => {
							setProcessing(true);

							searchResultsRef.current?.instance.clearSelection();

							const brandIDs = order.details.products?.map(x => x.brandID).filter(x => x).join(",");

							apiSearchMaterialOrderProducts(
								order.details.salesChannelID,
								searchCriteria.productLineID,
								searchCriteria.productTypeID,
								searchCriteria.sku,
								searchCriteria.productDescription,
								isLaminate ? brandIDs : undefined)
								.then((result) => {
									if (result.isSuccess)
										setSearchResults(result);
									else
										showErrorMessage(result.errorMessage!);
								})
								.finally(() => setProcessing(false));
						}}>
					</Button>
				</Item>
			</Box>

			<DataGrid
				ref={searchResultsRef}
				className="product-search-results"
				dataSource={searchResults?.products}
				height={'76%'}
				allowColumnResizing={true}
				wordWrapEnabled={true}
				keyExpr={['productID', 'retailProductID', 'mfrModel']}
				repaintChangesOnly={true}
				onSelectionChanged={(e) => {
					e.selectedRowKeys.forEach((key, index) => {
						if (!e.selectedRowsData[index].productRequestedDeliveryDate) {
							e.component.cellValue(e.component.getRowIndexByKey(key), "productRequestedDeliveryDate",
								getClosestDeliveryDate(e.selectedRowsData[index].deliveryDateSettings));
							e.component.saveEditData();
						}
					})
				}}
				onEditorPreparing={(e) => {
					if (e.dataField === 'productRequestedDeliveryDate') {
						e.editorOptions.disabledDates = (args: any) => {
							return !validateDeliveryDate(args.date, e.row?.data.deliveryDateSettings);
						}
					}

					if (e.row?.data.isColor) {

						// Set data source filtered by brandID 
						if (e.dataField === 'edgeID')
							e.editorOptions.dataSource = searchResults?.edges?.filter(x => x.brandID === e.row?.data.brandID);

						if (e.dataField === 'lengthID')
							e.editorOptions.dataSource = searchResults?.lengths?.filter(x => x.brandID === e.row?.data.brandID);

						if (e.dataField === 'miterID')
							e.editorOptions.dataSource = searchResults?.miters?.filter(x => x.brandID === e.row?.data.brandID);

						if (e.dataField === 'backerID')
							e.editorOptions.dataSource = searchResults?.backers?.filter(x => x.brandID === e.row?.data.brandID);

						if (e.dataField === 'substrateID')
							e.editorOptions.dataSource = searchResults?.substrates?.filter(x => x.brandID === e.row?.data.brandID);

						// Filter data source for Edge Depth by selected edge, the brand and the product type
						if (e.dataField === 'edgeDepthID') {
							e.editorOptions.onOpened = (ev: OpenedEvent) => {
								ev.component.option("dataSource",
									searchResults?.edgeDepths?.filter(x =>
										x.brandID === e.row?.data.brandID &&
										x.edgeID === e.row?.data.edgeID &&
										x.productTypeID === e.row?.data.productTypeID));
							};
						}
					}
					else {
						if (e.editorName === 'dxSelectBox')
							e.editorOptions.visible = false;
					}
				}}
				onRowUpdating={(e) => {
					if (!!e.newData.productRequestedDeliveryDate &&
						e.newData.productRequestedDeliveryDate !== e.oldData.productRequestedDeliveryDate &&
						!validateDeliveryDate(e.newData.productRequestedDeliveryDate, e.oldData.deliveryDateSettings)) {
						e.cancel = true;
						e.component.cancelEditData();
					}
				}}
			>
				<Paging enabled={false}></Paging>
				<Scrolling rowRenderingMode='virtual'></Scrolling>
				<Selection mode="multiple" allowSelectAll={false} />
				<Editing allowUpdating={true} mode={'cell'} refreshMode={'repaint'}></Editing>
				<Column
					dataField="sku"
					caption="House PO SKU"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField="mfrModel"
					caption="MFR SKU"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField="mfrDescription"
					caption="MFR Description"
					dataType="string"
					width={150}
					allowEditing={false}
				/>
				<Column
					dataField="description"
					caption={isLaminate ? 'Color/Finish' : 'Description'}
					dataType="string"
					width={150}
					allowEditing={false}
				/>
				<Column
					visible={!isLaminate}
					dataField="thickness"
					caption="Thickness"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					visible={!isLaminate}
					dataField="dimensions"
					caption="Dimensions"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					visible={!isLaminate}
					dataField="squareFootage"
					caption="Square Footage"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					visible={isLaminate}
					dataField="productType"
					caption="Product Type"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					visible={isLaminate}
					dataField="edgeID"
					caption="Edge"
					dataType="number"
					showEditorAlways={true}
					setCellValue={(newData: MOCatalogProduct, value: any, currentRowData: MOCatalogProduct) => {
						if (currentRowData.edgeID !== value) {
							newData.edgeID = value;
							newData.edgeDepthID = 0;
						}
					}}
					editorOptions={{
						valueExpr: 'productID',
						displayExpr: 'description',
						dropDownOptions: {
							minWidth: 200
						}
					}}
				>
					<Lookup />
				</Column>
				<Column
					visible={isLaminate}
					dataField="edgeDepthID"
					caption="Edge/Depth"
					dataType="number"
					showEditorAlways={true}
					editorOptions={{
						valueExpr: 'productID',
						displayExpr: 'description',
						dropDownOptions: {
							minWidth: 350
						}
					}}
				>
					<Lookup />
				</Column>
				<Column
					visible={isLaminate}
					dataField="lengthID"
					caption="Length"
					dataType="number"
					showEditorAlways={true}
					editorOptions={{
						valueExpr: 'productID',
						displayExpr: 'description'
					}}
				>
					<Lookup />
				</Column>
				<Column
					visible={isLaminate}
					dataField="miterID"
					caption="Miter"
					dataType="number"
					showEditorAlways={true}
					editorOptions={{
						valueExpr: 'productID',
						displayExpr: 'description'
					}}
				>
					<Lookup />
				</Column>
				<Column
					visible={isLaminate}
					dataField="backerID"
					caption="Backer"
					dataType="number"
					showEditorAlways={true}
					editorOptions={{
						valueExpr: 'productID',
						displayExpr: 'description'
					}}
				>
					<Lookup />
				</Column>
				<Column
					visible={isLaminate}
					dataField="substrateID"
					caption="Substrate"
					dataType="number"
					showEditorAlways={true}
					editorOptions={{
						valueExpr: 'productID',
						displayExpr: 'description',
						dropDownOptions: {
							minWidth: 150
						}
					}}
				>
					<Lookup />
				</Column>
				<Column
					dataField="distributorName"
					caption="Distributor"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					visible={!isLaminate}
					dataField="materialAttributes"
					caption="Material Attributes"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					visible={!isLaminate}
					caption="Image"
					width={70}
					cellRender={(cell) => {
						if (cell.data.swatchThumbnail)
							return <img src={'data:image/png;base64, ' + cell.data.swatchThumbnail} className='swatch-thumbnail' alt='' />
						else
							return <></>
					}}
				/>
				<Column
					visible={!isLaminate}
					dataField={'quantity'}
					caption="Quantity"
					width={100}
					dataType={'number'}
					editorOptions={{ showSpinButtons: true, min: 0, format: "##0.###" }}
					showEditorAlways={true}
				/>
				<Column
					dataField={'productRequestedDeliveryDate'}
					caption="Requested Delivery Date"
					width={100}
					dataType={'date'}
					editorOptions={{
						showClearButton: false,
						acceptCustomValue: false
					}}
					showEditorAlways={true}
				/>
			</DataGrid>

			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					elementAttr: { 'data-testid': 'btn-add-items' },
					text: 'Add Items',
					onClick: () => {
						let isValid = true;

						const selectedRows = searchResultsRef.current?.instance.getSelectedRowsData() as MOCatalogProduct[];

						if (selectedRows.length === 0) {
							alert("Please select at least one item", "Error");
							isValid = false;
						}

						for (let row of selectedRows) {
							if (!isLaminate && row.quantity <= 0) {
								alert("Quantity must be greater than 0", "Error");
								isValid = false;
								break;
							}

							if (isLaminate && row.isColor && (!row.edgeID || !row.edgeDepthID || !row.lengthID || !row.miterID || !row.backerID || !row.substrateID)) {
								alert("Some of the required options are missing for SKU " + row.sku, "Error");
								isValid = false;
								break;
							}

							if (isLaminate && row.productType === "ACCESSORIES" &&
								(!order.details.products || !order.details.products.map(x => x.brandID).filter(x => x).includes(row.brandID))) {
								alert("You must add countertop material prior to adding matching brand accessories.<br/>If you only need an accessory item, please reach out<br/> to your support specialist who can place the order for you.", "Error");
								isValid = false;
								break;
							}
						}

						if (isValid) {
							props.onProductsAdded(selectedRows);
							props.onPopupHidden();
							searchResultsRef.current?.instance.clearSelection();
						}
					}
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			>
			</ToolbarItem>
			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					elementAttr: { 'data-testid': 'btn-cancel' },
					text: 'Cancel',
					onClick: () => props.onPopupHidden()
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			>
			</ToolbarItem>
			<LoadPopupPanel visible={productLines.isLoading || productTypes.isLoading || processing}></LoadPopupPanel>
		</Popup>
	)
}

